import React, { Component } from 'react';
import PropTypes from 'prop-types';
import http from '../shared/http';
import PixelRow from './PixelRow';
import RtbPixel from '../shared/RtbPixel';
import StrModal from '../shared/StrModal';
import * as User from '../user';

const defaultErrorState = () => ({
  errors: ['An error occurred while creating this pixel. Please try again']
});

const buildCreativeState = creatives => {
  const creativeIds = creatives.map(creative => creative.id);

  const creativesById = creatives.reduce((acc, creative) => {
    acc[creative.id] = creative;
    return acc;
  }, {});

  return {
    creativeIds,
    creativesById
  };
};

const buildPixelsByCreativeId = (creatives, pixelUrls) => {
  const pixelsByCreativeId = {};

  creatives.forEach((creative, index) => {
    const correspondingPixelUrl = pixelUrls[index];
    pixelsByCreativeId[creative.id] = correspondingPixelUrl;
  });

  return pixelsByCreativeId;
};

class PixelExport extends Component {
  constructor(props) {
    super(props);

    this.createPixel = this.createPixel.bind(this);
    this.setCreativeData = this.setCreativeData.bind(this);
    this.buildState = this.buildState.bind(this);
    this.wrappedSetState = this.wrappedSetState.bind(this);
    this.state = {
      errors: [],
      creativeIds: [],
      pixelsByCreativeId: {},
      creativesById: {},
      currentUser: null
    };
  }

  componentDidMount() {
    this._mounted = true;
    User.getCurrent().then(currentUser => {
      this.setState({ currentUser: currentUser });
    });
    this.setCreativeData();
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  setCreativeData() {
    return Promise.resolve()
      .then(() => this.buildState(this.props.getCreatives()))
      .then(newState => {
        this.wrappedSetState({ ...newState });
      })
      .catch(() => {
        this.wrappedSetState(defaultErrorState());
      });
  }

  buildState(creatives) {
    const creativeStateData = buildCreativeState(creatives);

    const pixelUrlPromises = creatives.map(creative =>
      http
        .get(`/rtb_pixels/${creative.id}`)
        .then(resp =>
          resp && resp.data && resp.data.pixel_key
            ? RtbPixel.format(creative.action, resp.data.pixel_key)
            : null
        )
    );

    return Promise.all(pixelUrlPromises).then(pixelUrls => {
      const pixelsByCreativeId = buildPixelsByCreativeId(creatives, pixelUrls);
      return {
        ...creativeStateData,
        pixelsByCreativeId
      };
    });
  }

  wrappedSetState(...args) {
    if (!this._mounted) {
      return;
    }

    return this.setState(...args);
  }

  createPixel(creativeId) {
    const creative = this.state.creativesById[creativeId];
    this.wrappedSetState({ errors: [] });
    return http
      .post('/rtb_pixels', {
        preview_id: creative.id
      })
      .then(({ data }) => {
        this.wrappedSetState({
          pixelsByCreativeId: {
            ...this.state.pixelsByCreativeId,
            [creative.id]: RtbPixel.format(creative.action, data.pixel_key)
          }
        });
      })
      .catch(({ response }) => {
        if (response && response.data && response.data.errors) {
          this.wrappedSetState({
            errors: response.data.errors
          });
        } else {
          this.wrappedSetState(defaultErrorState());
        }
      });
  }

  render() {
    return (
      <StrModal
        modalClasses="export-modal"
        open={this.props.modalOpen}
        hideModal={this.props.hideModal}
        title="Impression Pixels"
      >
        {this.state.errors.length > 0 &&
          this.state.errors.map(error => (
            <div
              key={error}
              className="alert alert-danger"
              data-testid="pixel-export-error"
            >
              {error}
            </div>
          ))}

        {this.state.creativeIds.map(creativeId => (
          <PixelRow
            key={creativeId}
            creativeData={this.state.creativesById[creativeId]}
            createPixel={this.createPixel}
            pixelUrl={
              this.state.currentUser &&
              this.state.currentUser.featureEnabled('new_2_3_asset_swap')
                ? this.state.creativesById[creativeId].assetSwapUrl
                : this.state.pixelsByCreativeId[creativeId]
            }
          />
        ))}
      </StrModal>
    );
  }
}

PixelExport.propTypes = {
  getCreatives: PropTypes.func.isRequired,
  hideModal: PropTypes.func.isRequired,
  modalOpen: PropTypes.bool
};

PixelExport.defaultProps = {
  modalOpen: false
};

export default PixelExport;
